import type { ComponentPropsWithRef as ComponentProps } from 'react';
import React, { forwardRef, memo, useMemo } from 'react';

import useDebouncedState from 'common/hooks/useDebouncedState';
import { upCaseFirstLetter } from 'common/lib/stringHelper';
import ConditionalWrapper from 'common/utils/conditionalWrapper';
import type { ColorName } from 'ui/styles/config/colors';
import { z } from 'zod';

import Clickable from '../Clickable';
import type { BaseIcon, IconSize } from '../Icons/BaseIcon';
import type { SpacingProps } from '../Spacing';
import * as Styled from './Button.styled';

const emojiSchema = z.string().emoji();

export enum ButtonVariant {
  BRAND = 'brand',
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  GHOST = 'ghost',
  SUCCESS = 'success',
  DANGER = 'danger',
  MAKE_IT_POP = 'make-it-pop',
  LINK = 'link',
}

export type ButtonLabelSize = 'small' | 'regular';

type Extends = ComponentProps<'button'> & SpacingProps;

interface BaseProps extends Extends {
  'data-test-id'?: string;
  'data-dd-action'?: string;
  label: string;
  variant?: keyof typeof ButtonVariant;
  inverse?: boolean;
  size?: ButtonLabelSize;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  to?: string;
  iconColor?: ColorName;
  backgroundColor?: ColorName;
  iconSize?: keyof typeof IconSize;
}

export type ButtonIcon = typeof BaseIcon | string | React.ReactNode;

export interface ButtonWithIconsProps extends BaseProps {
  // limiting the type to only accept Icon or emoji
  startAdornment?: ButtonIcon;
  endAdornment?: ButtonIcon;
}

export interface ButtonWithIconOnlyProps extends BaseProps {
  Icon: ButtonIcon;
  badge: boolean;
  badgeColor?: ColorName;
}

export type ButtonProps = ButtonWithIconsProps | ButtonWithIconOnlyProps;

const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    const Icon = 'Icon' in props ? props.Icon : undefined;
    const badge = 'badge' in props ? props.badge : false;
    const badgeColor = 'badgeColor' in props ? props.badgeColor : undefined;
    const {
      label,
      variant = 'PRIMARY',
      inverse = false,
      size = 'regular',
      loading,
      disabled,
      fullWidth,
      to,
      iconColor: givenIconColor,
      backgroundColor,
      iconSize,
      margin,
      marginTop,
      marginBottom,
      marginLeft,
      marginRight,
      padding,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      ...restProps
    } = props;

    // Debounce loading by 100ms to allow for fade animation
    const debouncedLoading = useDebouncedState(loading, 100);
    const showLoading = loading || debouncedLoading;
    const loadingVisible = !!debouncedLoading;

    const spacingProps = {
      $margin: margin,
      $marginTop: marginTop,
      $marginBottom: marginBottom,
      $marginLeft: marginLeft,
      $marginRight: marginRight,
      $padding: padding,
      $paddingTop: paddingTop,
      $paddingBottom: paddingBottom,
      $paddingLeft: paddingLeft,
      $paddingRight: paddingRight,
    };

    const iconColor = useMemo(() => {
      if (givenIconColor) {
        return givenIconColor;
      }
      if (disabled && !inverse) {
        return 'iconDisabled';
      }
      if (disabled && inverse) {
        return 'iconInverseDisabled';
      }
      if (inverse) {
        return Styled.variantToInverseButtonIconColor[variant];
      }

      return Styled.variantToButtonIconColor[variant];
    }, [disabled, givenIconColor, inverse, variant]);

    const renderAdornment = ({
      adornment,
      key,
    }: {
      adornment: ButtonIcon;
      key?: 'start' | 'end';
    }) => {
      if (!adornment) {
        return null;
      }
      if (React.isValidElement(adornment)) {
        return adornment;
      }
      if (typeof adornment === 'function') {
        const Icon = adornment;
        return (
          <Icon
            className={
              key ? `button${upCaseFirstLetter(key)}Adornment` : undefined
            }
            color={iconColor}
            size={iconSize}
          />
        );
      }
      if (typeof adornment === 'string') {
        try {
          emojiSchema.parse(adornment);
          return adornment;
        } catch (e) {
          console.error(
            `Only emojis are supported as string \`${
              key === undefined
                ? 'Icon'
                : key === 'start'
                  ? 'startAdornment'
                  : 'endAdornment'
            }\` for Button`,
          );
        }
      }
      return null;
    };

    const a11yProps = {
      'aria-disabled': loading || disabled,
      'aria-label': loading ? 'Loading' : label,
    };

    /* The new version of the deprecated IconButton */
    if (Icon) {
      return (
        <Styled.IconOnlyButtonWrapper
          ref={ref}
          $inverse={inverse}
          $loading={loading}
          $size={size}
          $variant={variant}
          disabled={disabled}
          {...a11yProps}
          {...restProps}
          {...spacingProps}
        >
          <Styled.ButtonContentContainer
            $show={loading ? false : true}
            $size="small"
          >
            {renderAdornment({ adornment: Icon })}
          </Styled.ButtonContentContainer>
          {showLoading && <Styled.LoadingIcon $show={loadingVisible} />}
          {badge && <Styled.Badge $color={badgeColor} />}
        </Styled.IconOnlyButtonWrapper>
      );
    }

    const startAdornment = renderAdornment({
      adornment: 'startAdornment' in props && props.startAdornment,
      key: 'start',
    });
    const endAdornment = renderAdornment({
      adornment: 'endAdornment' in props && props.endAdornment,
      key: 'end',
    });
    const hasAdornment = Boolean(startAdornment || endAdornment);

    return (
      <ConditionalWrapper
        condition={!!to}
        wrapper={children => <Clickable to={to}>{children}</Clickable>}
      >
        <Styled.ButtonWrapper
          ref={ref}
          $backgroundColor={backgroundColor}
          $fullWidth={fullWidth}
          $inverse={inverse}
          $loading={loading}
          $size={size}
          $variant={variant}
          disabled={disabled}
          {...a11yProps}
          {...restProps}
          {...spacingProps}
        >
          <Styled.ButtonContentContainer
            $show={loading ? false : true}
            $size={size}
          >
            {startAdornment}
            {label ? (
              <Styled.ButtonLabel
                $disabled={disabled}
                $hasAdornment={hasAdornment}
                $size={size}
                className="buttonLabel"
              >
                {label}
              </Styled.ButtonLabel>
            ) : null}
            {endAdornment}
          </Styled.ButtonContentContainer>
          {showLoading && <Styled.LoadingIcon $show={loadingVisible} />}
        </Styled.ButtonWrapper>
      </ConditionalWrapper>
    );
  }),
);

export default Button;
